<template>
  <ion-header class="qbi-header">
    <ion-toolbar>
      <ion-buttons slot="start" class="left-group">
        <ion-button v-if="serviceStore.isSelected && serviceStore.selected.burger" class="header-btn" @click="handleHamburgerClick">
          <i class="fa-solid fa-bars"></i>
        </ion-button>

        <app-logo
          @click="handleNavigation(serviceStore.selected.url || '/')"
          :title="serviceStore.isSelected ? tUseCase(serviceStore.selected.label) : rootService ? tUseCase(rootService.label) : 'Heila Suite'"
          :logo="serviceStore.isSelected ? serviceStore.selected.logo : rootService ? rootService.logo : undefined"
        />
      </ion-buttons>

      <ion-buttons slot="end" class="right-group">
        <ion-button
            v-for="(link, index) in dynamicLinks"
            :key="'header-link-' + index"
            class="header-btn action-square-btn dynamic-header-link"
            @click="handleLinkAction(link.type, link.action)"
        >
          <img
              v-if="getIconData(link.icon).type === 'img'"
              :src="getIconData(link.icon).value"
              class="header-link-img"
          />
          <i v-else-if="getIconData(link.icon).type === 'fa-icon'" :class="`fa-solid ${getIconData(link.icon).value}`"></i>
          <ion-icon
              v-else
              :icon="getIconData(link.icon).value"
          />
        </ion-button>

        <ion-button
            v-if="userData.Id !== ''"
            id="grid-drawer-trigger"
            :class="['header-btn action-square-btn']"
            @click="onToggleDrawerHandler"
            fill="clear"
        >
          <i class="fa-solid fa-dice-d6"></i>
        </ion-button>
      </ion-buttons>
    </ion-toolbar>
  </ion-header>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import {
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  popoverController
} from '@ionic/vue';
import * as allIcons from 'ionicons/icons';
import AppLogo from "@/components/AppLogo.vue";

import {useServiceStore} from "@/stores/useServiceStore.ts";

import {useAuth} from "@/composables/useAuth.ts";
import {useNavigation} from "@/composables/useNavigation.ts";
import {useLocale} from "@/composables/useLocale.ts";
import {useServices} from "@/composables/useServices.ts";
import {LocalActionsRegistry} from "@/services/NavbarActions.ts";
import {toast} from "@/utils/notifications.ts";
import SendCommon from "@/services/IframeBridge/common/outbound.ts";
import { isNative } from "@/utils/platform.ts";

const { t, tUseCase } = useLocale();
const { goTo } = useNavigation();
const { rootService } = useServices();
const { userData } = useAuth();

const serviceStore = useServiceStore();

const props = defineProps({
  onToggleDrawer: {
    type: Function,
    required: true,
    default: () => {}
  }
});

const handleHamburgerClick = () => {
  handleLinkAction('postmessage', 'toggleSideBar')
};

const dynamicLinks = computed(() => {
  let links = rootService.value?.links || []
  if (serviceStore.isSelected && serviceStore.selected.links) links = [...serviceStore.selected.links, ...links]
  return links.filter((link: any) =>
      link.platform.includes(isNative ? 'mobile' : 'desktop')
  );
});

const onToggleDrawerHandler = () => {
  props.onToggleDrawer();
}

const clearAllPopups = async () => {
  let topOverlay = await popoverController.getTop();
  while (topOverlay) {
    await popoverController.dismiss();
    topOverlay = await popoverController.getTop(); // Check if there's another one behind it
  }
}

const handleNavigation = async (targetUrl: string) => {
  await clearAllPopups()
  await goTo(targetUrl);
};

const handleLinkAction = async (type: string, action: string) => {
  if (type === 'postmessage') {
    try {
      const actionKey = action as keyof typeof SendCommon;
      (SendCommon[actionKey] as any)({});
    } catch (err) {
      console.error(`Iframe Bridge: Failed to load outbound file with action "${action}"`, err);
    }
  } else if (type === 'function') {
    const executeLocalAction = LocalActionsRegistry[action];
    if (executeLocalAction) {
      const res = await executeLocalAction();
      if (!res.success) {
        await toast({ message: t(res.error.msg), preset: 'error' });
      }
    } else {
      console.warn(`Local function action "${action}" not found in LocalActionsRegistry.`);
    }
  }
};

const getIconData = (iconName: string) => {
  if (!iconName) return { type: 'icon', value: allIcons.helpCircleOutline };
  const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'];
  const isImage = imageExtensions.some(ext => iconName.toLowerCase().endsWith(ext));

  if (isImage) {
    return { type: 'img', value: iconName };
  }

  if (iconName.startsWith('fa-')) {
    return {
      type: 'fa-icon',
      value: iconName,
    }
  }

  const iconValue = (allIcons as any)[iconName];
  return {
    type: 'icon',
    value: iconValue || allIcons.helpCircleOutline // Fallback icon if not found
  };
};
</script>

<style>
/* Main Structural Framework Theme Variables Override */
.header-md {
  box-shadow: unset;
}

.qbi-header {
  --background: #ffffff;
  border-bottom: 1px solid #f1f5f9;
  box-shadow: 0 3px 6px #0000001f;
}

ion-toolbar {
  --background: #ffffff;
  --color: #363131;
  --padding-start: 12px;
  --padding-end: 12px;
  --min-height: var(--navbar-height);
}

/* Flex layout Alignment elements */
.left-group, .right-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.brand-group {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 4px;
}

/* Functional Buttons Configuration */
.header-btn {
  --color: #363131;
  --border-radius: 8px;
  --padding-start: 10px;
  --padding-end: 10px;
  font-size: 1.2rem;
  margin: 0;
}

/* Design Interactive Active State Boxes Selection */
.action-square-btn {
  --border-radius: 10px;
  height: 42px;
  width: 42px;
  transition: all 0.2s ease;
}

.action-square-btn.is-active,
.action-square-btn:active {
  --background: #e0f2fe !important; /* Soft premium light blue */
  --color: #0284c7;
}

.grid-icon-svg {
  width: 18px;
  height: 18px;
}

.user-profile-popover {
  --width: 290px;
  --border-radius: 20px; /* Smooth rounded container corners */
  --box-shadow: 0 12px 36px rgba(0, 0, 0, 0.06);
  --background: transparent;
}

.user-profile-popover ion-content {
  --background: transparent;
}

.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  /* Replicated the soft lime-to-teal gradient */
  background: linear-gradient(135deg, #81c784 0%, #4db6ac 100%);
  width: 52px;
  height: 52px;
  border-radius: 16px; /* Squircle appearance */
  color: #ffffff;
  font-weight: 600;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.popover-menu-list {
  background: #ffffff;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  padding: 12px 4px;
}

.menu-action-item {
  --background: #ffffff;
  --color: #2c2c2c;
  --inner-padding-end: 16px;
  --padding-start: 16px;
  --min-height: 54px; /* Generous breathing room per option */
  --border-style: none; /* Stripped out all list dividers */
  --inner-border-width: 0px;
  font-size: 1rem;
}

.menu-action-item i {
  color: #2c2c2c;
  font-size: 1.35rem;
  width: 24px;
  text-align: center;
}

</style>
